import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import { Reveal } from "@/components/Reveal";
import { useTitle } from "@/hooks/use-title";
import { WORK_OUTLINES } from "@/data/content";

export default function Work() {
  useTitle("Selected Work — Operating Reality");
  return (
    <main className="pt-16 lg:pt-[72px]" data-testid="work-page">
      <section className="relative overflow-hidden border-b border-line/50">
        <div className="bg-grid absolute inset-0 opacity-30" />
        <div className="wrap relative py-24 lg:py-32">
          <Reveal>
            <p className="kicker">Selected work</p>
            <h1 className="mt-6 max-w-3xl text-balance text-4xl font-semibold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              Transformation, documented honestly.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
              Every case study here is written the same way: the challenge, the operating
              reality, what changed, and the documented impact. No invented metrics, no
              decorated outcomes. Studies are published as they're cleared for release.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="wrap py-20 lg:py-28">
        <div className="grid gap-px border border-line/50 bg-line/50 md:grid-cols-2 lg:grid-cols-3">
          {WORK_OUTLINES.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.05} className="bg-ink">
              {w.href ? (
                <Link
                  to={w.href}
                  className="group flex h-full flex-col p-8 transition-colors duration-300 hover:bg-charcoal/40 lg:p-10"
                  data-testid={`work-study-${w.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">{w.scope}</p>
                    <span className="font-mono text-xs tracking-[0.25em] text-slate-700">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h2 className="mt-5 text-balance text-2xl font-medium leading-snug tracking-tight text-slate-100 transition-colors duration-300 group-hover:text-white lg:text-[1.7rem]">
                    {w.title}
                  </h2>
                  <p className="mt-auto flex items-center gap-2 pt-10 font-mono text-[10px] uppercase tracking-[0.28em] text-glow/80 transition-colors group-hover:text-glow">
                    Read the case study
                    <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </p>
                </Link>
              ) : (
                <article
                  className="group flex h-full flex-col p-8 transition-colors duration-300 hover:bg-charcoal/40 lg:p-10"
                  data-testid={`work-study-${w.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">{w.scope}</p>
                    <span className="font-mono text-xs tracking-[0.25em] text-slate-700">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h2 className="mt-5 text-balance text-2xl font-medium leading-snug tracking-tight text-slate-100 lg:text-[1.7rem]">
                    {w.title}
                  </h2>
                  <div className="mt-8 space-y-3 border-l border-line/60 pl-5">
                    {["The challenge", "The operating reality", "What changed", "Impact", "What this demonstrates"].map(
                      (part) => (
                        <p key={part} className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-600">
                          {part} <span className="text-slate-700">— in the full study</span>
                        </p>
                      )
                    )}
                  </div>
                  <p className="mt-auto pt-10 font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
                    Full case study — in preparation
                  </p>
                </article>
              )}
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-20 flex flex-col items-start gap-8 border border-line/50 bg-charcoal/20 p-8 lg:flex-row lg:items-center lg:justify-between lg:p-12">
            <SectionHeader
              kicker="Yours next?"
              title="Have a problem you can't quite explain?"
            />
            <Link to="/contact" className="btn-primary shrink-0" data-testid="work-contact-cta">
              Start a conversation <ArrowRight size={14} />
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
